import React from "react";
import "../DashboardStyles.css";

// FEMA National Risk Index, county-level dataset (accessed June 2026).
// VL = Very Low, RL = Relatively Low, RM = Relatively Moderate, RH = Relatively High
const HAZARDS = ["Drought", "Wildfire", "Hail", "Winter weather", "Tornado", "Ice storm", "Cold wave", "Strong wind"];
const NRI = [
  ["Banner",       "RM VL RL VL VL VL VL VL"],
  ["Box Butte",    "RH VL RM RM RL RL RL RL"],
  ["Cheyenne",     "RH VL RM RL RL RH RL RL"],
  ["Dawes",        "RM RL RL RM VL VL RL VL"],
  ["Deuel",        "RM VL RL VL VL VL VL VL"],
  ["Garden",       "RM VL RL VL VL VL VL VL"],
  ["Kimball",      "RM VL RM RL VL RM VL VL"],
  ["Morrill",      "RH RL RM RL VL RL RL RL"],
  ["Scotts Bluff", "RH RL RH RH RL RM RM RM"],
  ["Sheridan",     "RM RM RL RM VL VL RL VL"],
  ["Sioux",        "RM RL VL VL VL VL VL VL"],
];
const RATING_LABEL = {
  VL: "Very low",
  RL: "Relatively low",
  RM: "Relatively moderate",
  RH: "Relatively high",
};

function NriHeatmap() {
  return (
    <figure className="ceds-figure">
      <div className="table-wrapper">
        <table className="ceds-table nri-table">
          <thead>
            <tr>
              <th scope="col">County</th>
              {HAZARDS.map((h) => <th key={h} scope="col">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {NRI.map(([county, ratings]) => (
              <tr key={county}>
                <th scope="row" style={{ textAlign: "left" }}>{county}</th>
                {ratings.split(" ").map((r, i) => (
                  <td key={HAZARDS[i]} className={`nri nri-${r}`} title={`${HAZARDS[i]}: ${RATING_LABEL[r]}`}>
                    {r}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="nri-legend" aria-label="Risk rating legend">
        {Object.entries(RATING_LABEL).map(([k, v]) => (
          <span key={k}><i className={`nri-${k}`} /> {k} = {v}</span>
        ))}
      </div>
      <figcaption>
        Ratings are county-level percentile ranks relative to all U.S. counties
        nationally, not relative to each other within the Panhandle. Source:
        FEMA National Risk Index, county-level dataset.
      </figcaption>
    </figure>
  );
}

export default function Chapter10Dashboard() {
  return (
    <div className="chapter chapter10">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 10</p>
        <h2>Resiliency</h2>
        <p className="chapter-lede">
          Building economic resilience in the Nebraska Panhandle — how the
          region is exposed to disruption, what assets provide adaptive
          capacity, and how the plan reduces vulnerability over five years.
        </p>

        <h3>10.1 Introduction</h3>
        <p>
          Economic resilience — the capacity of a regional economy to
          anticipate, prepare for, withstand, and recover from disruptions — is
          particularly relevant to the Nebraska Panhandle, where geographic
          isolation, resource dependency, demographic fragility, and climate
          exposure combine to create a distinctive vulnerability profile. This
          chapter addresses how the region is exposed to disruption, how
          existing assets and institutions provide resilience capacity, and how
          the goals of the Regional Action Plan are designed to reduce
          vulnerability and strengthen adaptive capacity over the planning
          horizon.
        </p>

        <h3>10.2 Sources of Economic Vulnerability</h3>

        <h4>Agricultural income volatility</h4>
        <p>
          The most familiar source of disruption for the Panhandle. Commodity
          price cycles, drought years, and input cost spikes create significant
          year-to-year variation in farm income that ripples through retail
          sales, property tax receipts, agricultural-services employment, and
          the financial health of farm families simultaneously. Federal crop
          insurance and commodity support provide buffers but do not eliminate
          volatility or its local transmission.
        </p>

        <h4>Climate variability</h4>
        <p>
          An escalating concern. Drought frequency and severity are projected to
          increase in the High Plains under most climate scenarios, with direct
          implications for irrigated and dryland production, rangeland
          productivity, and the long-term viability of water-intensive systems.
          The Ogallala Aquifer’s non-renewable depletion means climate-driven
          increases in crop water demand cannot be offset indefinitely by more
          pumping. Managing this water-security challenge is the Panhandle’s
          most consequential resilience imperative — addressed in Goal 4,
          Objective 4.2.
        </p>

        <h4>Healthcare system fragility</h4>
        <p>
          Less visible than agricultural volatility but equally consequential.
          The financial fragility of the region’s Critical Access Hospitals —
          driven by declining reimbursement, provider shortages, and small
          patient volumes — means any of the six CAHs serving outlying counties
          could face closure under adverse conditions. The loss of a single CAH
          would simultaneously eliminate the largest employer in that community
          and emergency and inpatient care for a large geographic area.
        </p>

        <h4>Federal spending dependency</h4>
        <p>
          A systemic vulnerability cutting across sectors. The Panhandle economy
          is substantially supported by federal dollars — Medicare and Medicaid
          reimbursement, commodity programs and crop insurance, National Forest
          and Park Service employment and visitor spending, USDA rural
          development, and EDA and CDBG infrastructure funding. Any significant
          reduction in federal rural spending would hit the Panhandle
          disproportionately relative to more diversified regions.
        </p>

        <h4>Natural Hazard Exposure</h4>
        <p>
          FEMA’s National Risk Index provides a fifth, independently quantified
          vulnerability category this CEDS can name with precision rather than
          general description. The Index scores all eligible U.S. counties on
          relative risk across 18 natural hazard types, combining expected
          annual loss, social vulnerability, and community resilience into a
          single composite rating. By this measure, ten of the Panhandle’s
          eleven counties carry a “Very Low” overall risk rating nationally;
          Scotts Bluff County is the exception, rated “Relatively Low” — in the
          64th percentile nationally — reflecting its concentration of
          population and building value rather than unusually severe hazard
          exposure relative to its neighbors.
        </p>
        <p>
          The composite rating, however, obscures a more useful finding: which
          specific hazard drives expected losses varies meaningfully by county,
          and the pattern is not uniform across the region. Drought is the
          leading expected-loss hazard in eight of the eleven counties — Banner,
          Box Butte, Cheyenne, Deuel, Garden, Morrill, Sioux, and, closely behind
          wildfire, Sheridan — directly reinforcing this CEDS’s existing emphasis
          on Ogallala Aquifer depletion as the region’s most consequential
          long-term resilience concern (Section 10.2, Climate variability).
          Dawes and Sheridan counties are notable exceptions, where wildfire is
          the leading hazard, consistent with their position along the forested
          Pine Ridge escarpment. Scotts Bluff and Kimball counties are hail-led,
          the only two counties in the region where drought is not the top loss
          driver.
        </p>
        <p>
          One county-level finding is worth naming on its own, in the same
          spirit as Appendix D’s treatment of Banner County income data: Deuel
          County’s Social Vulnerability rating is “Relatively High” — in the
          94th percentile nationally — a sharp outlier against every
          neighboring Panhandle county, which rate “Very Low” to “Relatively
          Moderate.” This does not indicate Deuel County faces more frequent or
          severe hazards than its neighbors; rather, it reflects the demographic
          and socioeconomic factors the Index uses to measure a community’s
          capacity to prepare for, respond to, and recover from a hazard once it
          occurs — factors that, in a county of under 1,900 residents, can shift
          the rating sharply on the strength of a small population’s
          characteristics. Consistent with the regional-lens approach this CEDS
          takes throughout, this finding is best read as a flag for further
          local engagement rather than a precise measure of Deuel County’s risk
          in isolation.
        </p>
        <p>
          The Panhandle’s low exposure stands out against Nebraska’s statewide
          profile. At the state level, the National Risk Index rates Nebraska
          “Relatively Low” for overall expected annual loss — 35th among the 50
          states and U.S. territories the Index scores, putting Nebraska roughly
          in the middle of the national distribution. But Nebraska’s statewide
          rating is driven by hazards rated “Relatively High” nationally (hail,
          drought, strong wind, tornado, and winter weather, in that order of
          severity) while the Panhandle counties profiled above register “Very
          Low” to “Relatively Moderate” on most of these same hazard types. The
          likeliest explanation is geographic: Nebraska’s statewide hail and
          tornado exposure is concentrated along the more humid,
          severe-storm-prone corridor of central and eastern Nebraska, not the
          High Plains. For the Panhandle specifically, this means the region
          carries a smaller share of the hazard exposure that defines Nebraska’s
          statewide risk profile. This is a point this CEDS notes as a genuine,
          if modest, regional asset, while still treating drought and aquifer
          depletion (the Panhandle’s own leading hazard, per the county data
          above) as the resilience priority it already is in this plan.
        </p>
        <p className="source-note">
          Source note: FEMA National Risk Index data, county-level and
          state-level datasets, accessed June 2026, hazards.fema.gov/nri. Ratings
          are relative to all U.S. counties (for county data) or all U.S. states
          and territories (for state data) nationally — a county is not rated
          relative to Nebraska, and Nebraska is not rated relative to other
          states’ Panhandle-equivalent regions. A “Very Low” county rating means
          low risk compared to the full range of U.S. counties, not the absence
          of hazard exposure.
        </p>

        <NriHeatmap />

        <h3>10.3 Resilience Assets and Institutional Capacity</h3>
        <p>
          Against these vulnerabilities, the Panhandle possesses genuine
          resilience assets. Nebraska’s Natural Resources District system —
          locally governed, financially autonomous, and technically capable —
          provides groundwater governance that has protected the region’s water
          supply better than most neighboring states. The diversified
          agricultural base — spanning irrigated specialty crops, dryland
          farming, and extensive cattle ranching — provides more resilience than
          a monocrop economy would. And the depth of community social capital
          documented in Chapter 6 means that when disruptions occur, communities
          respond collectively in ways more atomized populations cannot.
        </p>

        <div className="callout">
          <span className="callout-label">PADD is itself a resilience asset</span>
          <p>
            As the region’s designated Economic Development District under EDA,
            PADD has the institutional mandate, partner relationships,
            grant-administration capacity, and cross-jurisdictional perspective
            needed to coordinate regional responses to disruption.
            Strengthening PADD’s capacity — in staff, data systems, and partner
            relationships — is simultaneously a development goal and a
            resilience investment.
          </p>
        </div>

        <h3>10.4 Resilience-Oriented Strategy Principles</h3>
        <p>
          The CEDS goals embed resilience principles throughout.{" "}
          <strong>Diversification</strong> — reducing dependence on any single
          sector, employer, or revenue source — is the foundational strategy;
          the six target sectors in Chapter 5 and Goal 4 are explicitly chosen
          to diversify the base relative to its current concentration in
          agriculture, healthcare, and government. <strong>Redundancy</strong> —
          backup capacity in critical systems — is reflected in the tiered
          water/wastewater pipeline (Goal 3.1) and the broadband buildout (Goal
          3.2), which builds alternative connectivity in communities dependent
          on a single provider. <strong>Adaptive capacity</strong> — the ability
          to identify and respond to emerging threats — is built through the
          regional coordination structure in Goal 4.1: the annual review, the
          public dashboard, and the Council’s regular convening create a
          mechanism for early identification and rapid strategy adjustment.
        </p>

        <h3>10.5 Federal Resilience Program Alignment</h3>
        <p>
          EDA’s economic resilience framework aligns directly with this CEDS.
          EDA’s CEDS requirements specifically call for resilience analysis
          addressing the region’s vulnerability to disruption and its capacity
          to respond; the goals, objectives, and performance measures in Chapter
          9 are structured to meet those requirements while producing genuine
          strategic value. Additional relevant federal programs include USDA’s
          Rural Economic Development programs, the CDBG Disaster Recovery
          program, FEMA’s Hazard Mitigation Grant Program, and SBA disaster
          assistance. PADD will maintain awareness of these programs and ensure
          Panhandle communities are positioned to access them when disruptions
          occur.
        </p>

        <h3>10.6 Annual Resilience Review</h3>
        <p>
          Each annual CEDS review will include a resilience assessment
          addressing: significant economic disruptions in the preceding year and
          the region’s response; changes in the vulnerability profile in Section
          10.2; progress on resilience-oriented initiatives in Goals 3 and 4;
          and emerging threats identified by regional stakeholders. This
          assessment will be presented to the CEDS Strategy Council and included
          in the public-facing progress report published after each review.
        </p>
      </div>
    </div>
  );
}
